import { useEffect, useState, useCallback, useRef } from "react";
import { GroupChannel, GroupChannelHandler } from "@sendbird/chat/groupChannel";
import { ConnectionHandler } from "@sendbird/chat";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { useQueryClient } from "@tanstack/react-query";
import { useMessageNotificationSound } from "./use-message-notification-sound";
import { INTERNAL_OPS_CUSTOM_TYPES } from "@/lib/constants";
import { useTrackEvent, PostHogEvents } from "@/lib/posthog-events";

interface UseChannelsListProps {
  customTypesFilter?: string[];
  enabled?: boolean;
  /**
   * Controls sound + analytics side effects on `onMessageReceived`. Defaults
   * to true so callers that own the conversations UI keep firing. Background
   * subscribers (e.g. the global sidebar badge) should pass `false` to avoid
   * double-firing alongside the conversations page.
   */
  enableNotifications?: boolean;
}

export function useChannelsList({
  customTypesFilter = [],
  enabled = true,
  enableNotifications = true,
}: UseChannelsListProps = {}) {
  const [channels, setChannels] = useState<GroupChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastVisibilityChange = useRef<number>(Date.now());
  const isReconnecting = useRef<boolean>(false);
  const syncChannelsRef = useRef<() => Promise<void>>();

  const store = useSendbirdStateContext();
  const sdk = enabled ? store?.stores?.sdkStore?.sdk : null;
  const { playTeenMessageSound, playOpsMessageSound } =
    useMessageNotificationSound();
  const { track } = useTrackEvent();
  const trackRef = useRef(track);
  trackRef.current = track;
  const notificationsEnabledRef = useRef(enableNotifications);
  notificationsEnabledRef.current = enableNotifications;

  // Invalidate mentorship list when a new Sendbird channel appears so BE-side
  // data (e.g. magic moment Day 1 state) is refetched without a manual reload.
  const queryClient = useQueryClient();
  const invalidateMentorshipsOnNewChannel = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["mentorship"] });
  }, [queryClient]);

  const fetchChannels = useCallback(async () => {
    if (!sdk) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const query = await sdk.groupChannel?.createMyGroupChannelListQuery({
        order: "latest_last_message",
        includeFrozen: false,
        includeEmpty: true,
        customTypesFilter: customTypesFilter,
        limit: 100,
      });

      const allChannels: GroupChannel[] = [];
      if (query) {
        while (query.hasNext) {
          const page = await query.next();
          if (!page || page.length === 0) break;
          allChannels.push(...page);
        }
      }

      allChannels.sort((a, b) => {
        const aTime = a.lastMessage?.createdAt || 0;
        const bTime = b.lastMessage?.createdAt || 0;
        return bTime - aTime;
      });

      setChannels(allChannels);
    } catch (err) {
      console.error("Error fetching channels:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch channels");
    } finally {
      setLoading(false);
    }
  }, [sdk, customTypesFilter]);

  const syncChannelsAfterReconnection = useCallback(async () => {
    if (!sdk || isReconnecting.current) return;

    // Check connection state before attempting sync
    const connectionState = sdk.connectionState;
    if (connectionState !== "OPEN") {
      console.log(
        `Connection not ready (state: ${connectionState}), skipping sync`,
      );
      return;
    }

    isReconnecting.current = true;
    console.log("Syncing channels after reconnection...");

    try {
      // Clear any previous connection errors
      setError(null);
      await fetchChannels();
      console.log("Channel sync completed after reconnection");
    } catch (error) {
      console.error("Failed to sync channels after reconnection:", error);
      setError("Failed to sync messages. Please refresh the page.");
    } finally {
      isReconnecting.current = false;
    }
  }, [fetchChannels, sdk]);

  // Store the sync function in a ref to avoid dependency issues
  syncChannelsRef.current = syncChannelsAfterReconnection;

  useEffect(() => {
    if (!sdk) {
      setLoading(false);
      return;
    }

    // Generate unique handler IDs
    const channelHandlerId = `channel-list-${Date.now()}-${Math.random()}`;
    const connectionHandlerId = `connection-${Date.now()}-${Math.random()}`;

    // Use timeout to ensure SDK is fully initialized (similar to _authenticated.tsx pattern)
    const timeout = setTimeout(async () => {
      try {
        // Connection handler for managing reconnection
        const connectionHandler = new ConnectionHandler({
          onReconnectStarted: () => {
            console.log("Reconnection started...");
            setError(null); // Clear previous errors during reconnection attempt
          },
          onReconnectSucceeded: async () => {
            console.log("Reconnection succeeded, syncing channels...");
            setError(null);
            // Add small delay to ensure connection is stable
            setTimeout(async () => {
              await syncChannelsRef.current?.();
            }, 1000);
          },
          onReconnectFailed: () => {
            console.log("Reconnection failed");
            setError(
              "Connection lost. Please check your network and refresh the page.",
            );
          },
        });

        const matchesFilter = (groupChannel: GroupChannel) =>
          customTypesFilter.length === 0 ||
          customTypesFilter.includes(groupChannel.customType ?? "");

        const channelHandler = new GroupChannelHandler({
          onMessageReceived: (channel, message) => {
            const groupChannel = channel as GroupChannel;

            if (!matchesFilter(groupChannel)) return;

            if (notificationsEnabledRef.current) {
              const senderId = (message as any)?.sender?.userId;
              const isFromSelf =
                senderId && senderId === sdk.currentUser?.userId;

              if (!isFromSelf) {
                if (groupChannel.customType === "MENTORSHIP") {
                  playTeenMessageSound();

                  if (senderId) {
                    trackRef.current(PostHogEvents.TEEN_MESSAGE_RECEIVED, {
                      teen_id: senderId,
                      channel_url: groupChannel.url,
                      message_type: (message as any)?.customType || "text",
                    });
                  }
                } else if (
                  (INTERNAL_OPS_CUSTOM_TYPES as readonly string[]).includes(
                    groupChannel.customType ?? "",
                  )
                ) {
                  playOpsMessageSound();
                }
              }
            }

            setChannels((prevChannels) => {
              const existingIndex = prevChannels.findIndex(
                (ch) => ch.url === groupChannel.url,
              );
              let nextChannels: GroupChannel[];
              if (existingIndex === -1) {
                nextChannels = [groupChannel, ...prevChannels];
                invalidateMentorshipsOnNewChannel();
              } else {
                nextChannels = prevChannels.map((ch) =>
                  ch.url === groupChannel.url ? groupChannel : ch,
                );
              }
              return nextChannels.sort((a, b) => {
                const aTime = a.lastMessage?.createdAt || 0;
                const bTime = b.lastMessage?.createdAt || 0;
                return bTime - aTime;
              });
            });
          },
          onChannelChanged: (channel) => {
            const groupChannel = channel as GroupChannel;
            if (!matchesFilter(groupChannel)) return;
            setChannels((prevChannels) => {
              const exists = prevChannels.some(
                (ch) => ch.url === groupChannel.url,
              );
              if (!exists) invalidateMentorshipsOnNewChannel();
              const updated = exists
                ? prevChannels.map((ch) =>
                    ch.url === groupChannel.url ? groupChannel : ch,
                  )
                : [groupChannel, ...prevChannels];
              return updated;
            });
          },
          onChannelDeleted: (channelUrl) => {
            setChannels((prevChannels) =>
              prevChannels.filter((ch) => ch.url !== channelUrl),
            );
          },
        });

        // Add the connection handler
        if (typeof sdk?.addConnectionHandler === "function") {
          sdk.addConnectionHandler(connectionHandlerId, connectionHandler);
        } else {
          console.error("Failed to add connectionHandler - SDK not ready");
        }

        // Add the channel handler
        if (typeof sdk?.groupChannel?.addGroupChannelHandler === "function") {
          sdk.groupChannel.addGroupChannelHandler(
            channelHandlerId,
            channelHandler,
          );
        } else {
          console.error("Failed to add groupChannelHandler - SDK not ready");
        }

        // Initial fetch
        await fetchChannels();
      } catch (error) {
        console.error("Error setting up channel list handlers:", error);
      }
    }, 100); // Wait 100ms for SDK to be ready

    // Cleanup
    return () => {
      clearTimeout(timeout);
      try {
        if (typeof sdk?.removeConnectionHandler === "function") {
          sdk.removeConnectionHandler(connectionHandlerId);
        }
        if (
          typeof sdk?.groupChannel?.removeGroupChannelHandler === "function"
        ) {
          sdk.groupChannel.removeGroupChannelHandler(channelHandlerId);
        }
      } catch (error) {
        console.error("Failed to remove handlers:", error);
      }
    };
  }, [
    sdk,
    JSON.stringify(customTypesFilter),
    playTeenMessageSound,
    playOpsMessageSound,
    invalidateMentorshipsOnNewChannel,
  ]);

  // Page visibility API integration for handling background/foreground transitions
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden && sdk) {
        const timeSinceLastChange = Date.now() - lastVisibilityChange.current;
        // If the page was hidden for more than 30 seconds, sync channels
        if (timeSinceLastChange > 30000) {
          console.log(
            `Page visible after ${timeSinceLastChange}ms, syncing channels...`,
          );
          await syncChannelsRef.current?.();
        }
      }
      lastVisibilityChange.current = Date.now();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [sdk]);

  return {
    channels,
    loading,
    error,
  };
}
